import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useExamsWithoutResults, useExamsWithoutResultsCount } from '../../../hooks/useWidgetData';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { Table } from 'antd';
import Globals from '../../../config/globals';
import PatientExamPane from '../../Exams/PatientExamPane';
import { calculateAge } from '../../Helpers/PatientHelper';
import { getPatientExamResults } from '../../../common/services/ExamService';

/**
 * ExamsWithoutResultsWidget component
 * Shows exams that are 21+ days old without results
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 */
export default function ExamsWithoutResultsWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState }) {
    const queryClient = useQueryClient();

// Determine when to load full data: either expanded in card OR in fullscreen mode
const shouldLoadFullData = !isCollapsed || isExpanded;

// Fetch count when collapsed AND not expanded
const { data: countData, isLoading: isLoadingCount } = useExamsWithoutResultsCount(isCollapsed && !isExpanded);

// Fetch full data when expanded in card or fullscreen
const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch 
} = useExamsWithoutResults(shouldLoadFullData);

const results = data?.data || [];
const count = isCollapsed ? countData : data?.count;

    const [showAddExamPane, setShowAddExamPane] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedPatientExam, setSelectedPatientExam] = useState(null);
    const [examResults, setExamResults] = useState(null);
    const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
    const [facility, setFacility] = useState(null);

// Notify parent of loading state for fullscreen overlay
useEffect(() => {
    if (setIsLoading) {
        setIsLoading(isLoading || isLoadingCount);
    }
}, [isLoading, isLoadingCount, setIsLoading]);

// Show error toast when expanded and error occurs
useEffect(() => {
    if (!isCollapsed && isError) {
        toast.error(`Failed to load exams: ${error?.message || 'Unknown error'}`);
    }
}, [isCollapsed, isError, error]);

// Set refetch function in shared state when it becomes available
useEffect(() => {
    if (setSharedState && refetch) {
        setSharedState(prev => ({ ...prev, refetch }));
    }
}, [setSharedState, refetch]);

    /**
     * Calculate days since exam - memoized
     */
    const getDaysSinceExam = useCallback((examDateTime) => {
        const examDate = new Date(examDateTime);
        const today = new Date();
        const diffMs = today - examDate;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays;
    }, []);

    /**
     * Format date to short format - memoized
     */
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'numeric', 
            day: 'numeric', 
            year: 'numeric' 
        });
    }, []);

    /**
     * Format MRN with spaces - memoized
     */
    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    const handleViewExam = useCallback(async (row) => {
        try {
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            if (!row.patientExamId) {
                toast.error('Missing exam information');
                return;
            }

            if (!row.facilityId) {
                toast.warning('Facility information missing from API. Please contact support.');
                console.error('Missing facilityId in API response. Row:', row);
                return;
            }

            const postdata = {
                uniqueID: row.patientExamId,
                facilityID: row.facilityId,
                name: row.examClass,
                userID: userInfo.userId
            };

            const data = await getPatientExamResults(postdata);

            if (!data) {
                throw new Error('No data returned from server');
            }

            setSelectedPatient({ dob: row.dob || data.patientDob });
            setSelectedPatientExam(data.examInfo);
            setExamResults(data.resultInfo);
            setSharedFollowUpIDs(data.examIDs);
            setFacility({ facilityID: row.facilityId, facilityName: row.facilityName });
            setShowAddExamPane(true);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, []);

    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'exams-without-results'] });
        toast.success('Widget data refreshed');
    }, [queryClient]);

    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy('updating');
        setShowAddExamPane(false);
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'exams-without-results'] });
        toast.success('Widget data refreshed');
    }, [queryClient]);

    /**
     * Table columns configuration - memoized to prevent recreation
     */
    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            sorter: (a, b) => (a.patientName || '').localeCompare(b.patientName || ''),
            width: 200,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace' }}>{formatMRN(mrn)}</span>,
            width: 150,
        },
        {
            title: 'DOB',
            dataIndex: 'dob',
            key: 'dob',
            render: (dob) => formatDate(dob),
            sorter: (a, b) => new Date(a.dob) - new Date(b.dob),
            width: 120,
        },
        {
            title: 'Provider',
            dataIndex: 'providerName',
            key: 'providerName',
            sorter: (a, b) => (a.providerName || '').localeCompare(b.providerName || ''),
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            sorter: (a, b) => (a.facilityName || '').localeCompare(b.facilityName || ''),
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (examDateTime) => formatDate(examDateTime),
            sorter: (a, b) => new Date(a.examDateTime) - new Date(b.examDateTime),
            defaultSortOrder: 'ascend',
            width: 120,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            sorter: (a, b) => (a.examType || '').localeCompare(b.examType || ''),
            ellipsis: true,
            width: 150,
        },
        {
            title: 'Days Ago',
            key: 'daysAgo',
            render: (_, record) => {
                const daysAgo = getDaysSinceExam(record.examDateTime);
                const isUrgent = daysAgo > 30;
                return (
                    <span
                        style={{
                            color: isUrgent 
                                ? (Globals.theme === 'dark' ? '#f87171' : '#dc2626')
                                : (Globals.theme === 'dark' ? '#fb923c' : '#ea580c'),
                            fontWeight: 500
                        }}
                    >
                        {daysAgo}
                    </span>
                );
            },
            sorter: (a, b) => getDaysSinceExam(a.examDateTime) - getDaysSinceExam(b.examDateTime),
            width: 100,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleViewExam(row);
                    }}
                    className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1"
                    style={{
                        backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#db2777' : '#be185d';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                            Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                    }}
                    title="View exam"
                >
                    View
                    <ExternalLink size={14} />
                </button>
            )
        },
    ], [formatDate, formatMRN, getDaysSinceExam, handleViewExam]);

    // COLLAPSED STATE - Show only count
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div 
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777' }}
                    />
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div 
                    className="text-5xl font-light"
                    style={{ color: Globals.theme === 'dark' ? '#ec4899' : '#db2777' }}
                >
                    {count.toLocaleString()}
                </div>
                <div 
                    className="text-sm mt-2"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    exams without results
                </div>
            </div>
        );
    }

    // EXPANDED STATE - Show full data
    if (isError) {
        return (
            <div className="flex flex-col items-center justify-center py-4 px-4">
                <AlertCircle 
                    className="w-12 h-12 mb-4"
                    style={{ color: Globals.theme === 'dark' ? '#f87171' : '#dc2626' }}
                />
                <p 
                    className="text-sm mb-4"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    Failed to load exams
                </p>
                <button
                    onClick={() => refetch()}
                    className="px-4 py-2 rounded-lg font-medium transition-colors"
                    style={{
                        backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                        color: 'white'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777'}
                >
                    Retry
                </button>
            </div>
        );
    }

    // Success - no exams
    if (count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle 
                    className="w-12 h-12 mb-4"
                    style={{ color: Globals.theme === 'dark' ? '#4ade80' : '#16a34a' }}
                />
                <p 
                    className="text-lg font-medium mb-2"
                    style={{ color: Globals.theme === 'dark' ? '#e4e4e7' : '#18181b' }}
                >
                    All caught up!
                </p>
                <p 
                    className="text-sm"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    No exams are waiting for results
                </p>
            </div>
        );
    }

    // Has results to display
    return (
        <>
        <div className="space-y-4">
            {/* Ant Design Table */}
            <Table
                columns={columns}
                dataSource={results}
                rowKey="patientExamId"
                pagination={{
                    defaultPageSize: 50,
                    pageSizeOptions: ['25', '50', '100', '200'],
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} exams`,
                }}
                size="small"
                scroll={{ y: 500 }}
                virtual
                loading={isLoading}
            />
        </div>

        {showAddExamPane && selectedPatient && (
            <PatientExamPane
                patientAge={calculateAge(selectedPatient?.dob)}
                patientInactive={false}
                patient={selectedPatient}
                showPane={showAddExamPane}
                examInfo={selectedPatientExam}
                examHistory={examResults}
                facility={facility}
                updateExamInfo={updateExamInfo}
                from='no-results-entered'
                hidePane={() => setShowAddExamPane(false)}
                sharedFollowUpIDs={sharedFollowUpIDs}
                getNewExamInfo={getNewExamInfo}
            />
        )}
        </>
    );
}
